import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Calendar, Settings, List, CalendarDays } from "lucide-react";
import CalendarIntegrations from "./CalendarIntegrations";
import AppointmentsList from "./AppointmentsList";
import AppointmentSettings from "./AppointmentSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Appointments() {
  const [searchParams] = useSearchParams();
  const [actors, setActors] = useState([]);
  const [selectedActorId, setSelectedActorId] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("appointments");

  useEffect(() => {
    // Check for OAuth callback params
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const actorId = searchParams.get("actor_id");

    if (success === "true") {
      toast.success("Google Calendar connected successfully!");
      if (actorId) setSelectedActorId(actorId);
    }
    if (error) {
      toast.error(`Failed to connect calendar: ${error}`);
    }

    fetchActors();
  }, [searchParams]);

  const fetchActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${BACKEND_URL}/api/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // API returns array directly
      const actorsList = Array.isArray(response.data) ? response.data : (response.data.actors || []);
      setActors(actorsList);
      
      // Auto-select first actor if none selected
      if (!selectedActorId && actorsList.length > 0) {
        setSelectedActorId(actorsList[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch actors:", error);
      toast.error("Failed to load AI Actors");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="w-8 h-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-slate-900">Appointments</h1>
        </div>
        <p className="text-slate-600">
          Manage calendar integrations and appointment bookings for your AI Actors
        </p>
      </div>

      {/* Actor Selector */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-slate-700">Select AI Actor:</label>
            <select
              value={selectedActorId}
              onChange={(e) => setSelectedActorId(e.target.value)}
              className="flex-1 max-w-md px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select an AI Actor...</option>
              {actors.map((actor) => (
                <option key={actor.id} value={actor.id}>
                  {actor.name}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {selectedActorId ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1">
            <TabsTrigger value="appointments" className="flex items-center gap-2 data-[state=active]:bg-white">
              <List className="w-4 h-4" />
              Appointments
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Calendar className="w-4 h-4" />
              Calendar Integrations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            <AppointmentsList actorId={selectedActorId} />
          </TabsContent>

          <TabsContent value="settings">
            <AppointmentSettings actorId={selectedActorId} />
          </TabsContent>

          <TabsContent value="integrations">
            <CalendarIntegrations actorId={selectedActorId} />
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Select an AI Actor</h3>
            <p className="text-slate-500">Choose an AI Actor to manage their appointments and calendar integrations</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
